// Typed, namespaced wrapper around localStorage.
//
// Replaces ad-hoc localStorage.getItem/setItem + manual JSON and "=== 'true'"
// coercion scattered across controllers (chat_dock, email_layout, calendar…).
//
// Usage:
//   import { persistedState } from "../lib/persisted_state"
//
//   const dockState = persistedState("chatDock", { collapsed: true })
//   dockState.get("collapsed")          // → boolean (typed, with default)
//   dockState.set("collapsed", false)   // → persists JSON
//   dockState.subscribe("collapsed", (value) => {...}) // cross-tab updates
//
// Values are JSON-encoded, so booleans/numbers/objects round-trip correctly
// instead of becoming strings. All keys are namespaced (e.g. "chatDock.collapsed").

const PREFIX = "tsh:"

function storageKey(namespace, key) {
  return `${PREFIX}${namespace}.${key}`
}

function read(fullKey, fallback) {
  try {
    const raw = localStorage.getItem(fullKey)
    if (raw === null) return fallback
    return JSON.parse(raw)
  } catch {
    return fallback
  }
}

function write(fullKey, value) {
  try {
    localStorage.setItem(fullKey, JSON.stringify(value))
  } catch {
    // Storage full or unavailable (private mode) — degrade gracefully.
  }
}

export function persistedState(namespace, defaults = {}) {
  return {
    get(key) {
      return read(storageKey(namespace, key), defaults[key])
    },

    set(key, value) {
      write(storageKey(namespace, key), value)
      return value
    },

    remove(key) {
      try { localStorage.removeItem(storageKey(namespace, key)) } catch {}
    },

    // Fires when the value changes in *another* tab (storage event).
    // Returns an unsubscribe function — call it in disconnect().
    subscribe(key, callback) {
      const fullKey = storageKey(namespace, key)
      const handler = (event) => {
        if (event.key !== fullKey) return
        try {
          callback(event.newValue === null ? defaults[key] : JSON.parse(event.newValue))
        } catch {
          callback(defaults[key])
        }
      }
      window.addEventListener("storage", handler)
      return () => window.removeEventListener("storage", handler)
    },
  }
}

export default persistedState
